import React, { useState, useEffect } from 'react';
import { Button, Radio, message, Space, Dropdown, Menu, Checkbox, Popover, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams, Outlet } from 'react-router-dom';
import { fetchCandidates, updateCandidate, createCandidate, deleteCandidate, updateCandidateStatusOptimistic, revertCandidateStatus, fetchCandidateById } from '../../redux/candidatesSlice';
import { fetchVacanciesFunnelStatuses, fetchVacancies } from '../../redux/vacanciesSlice';
import { updateVacancyCandidateStatus } from '../../redux/vacancyCandidatesSlice';
import { fetchUsers } from '../../redux/userOrganisationsSlice';
import CandidateList from '../../components/Candidates/CandidateList';
import CandidateKanban from '../../components/Candidates/CandidateKanban';
import CreateModal from '../../components/Candidates/CreateModal'
import EditModal from '../../components/Candidates/EditModal'
import CandidateViewModal from '../../components/Candidates/CandidateViewModal';
import ParserModal from '../../components/Candidates/ParserModal';
import CompletionModal from '../../components/Candidates/CompletionModal';
import styles from '../../components/Candidates/Candidates.module.css';
import { columnLabels } from '../../constants/candidateColumnLabels'
import { SettingOutlined, DownOutlined } from '@ant-design/icons';
import { isLeaderRole } from '../../utils/candidatePermissions';

const allColumnKeys = Object.keys(columnLabels);

const defaultColumns = [
  'id',
  'full_name',
  'vacancy_and_status',
  'phone_number',
  'files',
  'salary_range',
  'actions'
];

const CandidatesPage = () => {
  const { user, orgId } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const { list: candidates = [], loading: candidatesLoading, current: currentCandidate, totalPages, totalItems } = useSelector(state => state.candidates);
  const { list: vacancies = [], loading: vacanciesLoading } = useSelector(state => state.vacancies);
  const { list: users = [], loading: usersLoading } = useSelector(state => state.userOrganisations);
  const { funnelStatuses = [] } = useSelector(state => state.vacancies);
  const [searchParams, setSearchParams] = useSearchParams();

  // Состояние пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Состояние фильтров
  const [filters, setFilters] = useState({});

  const [viewMode, setViewMode] = useState('list');
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [parserModalVisible, setParserModalVisible] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState(defaultColumns);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [columnsDropdownOpen, setColumnsDropdownOpen] = useState(false);
  const [statusesDropdownOpen, setStatusesDropdownOpen] = useState(false);
  const [completionModalVisible, setCompletionModalVisible] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  const storedRole = typeof window !== 'undefined' ? localStorage.getItem('user_role') : null;
  const effectiveRole = user?.role || storedRole;
  const isLeader = isLeaderRole(effectiveRole);

  useEffect(() => {
    dispatch(fetchVacanciesFunnelStatuses());
    dispatch(fetchUsers());
    dispatch(fetchVacancies({ skip: 0, limit: 20 }));
  }, [dispatch]);

  useEffect(() => {
    const skip = (currentPage - 1) * pageSize;

    // Преобразуем фильтры для бэкенда в нормальном формате
    const backendFilters = {};
    Object.keys(filters).forEach(key => {
      const value = filters[key];

      if (value === null || value === undefined || value === '') {
        return;
      }

      const backendKey = key;

      if (Array.isArray(value)) {
        const filteredArray = value.filter(v => v !== null && v !== undefined && v !== '');
        if (filteredArray.length > 0) {
          backendFilters[backendKey] = filteredArray.join(',');
        }
      } else if (typeof value === 'string' || typeof value === 'number') {
        backendFilters[backendKey] = value;
      } else if (typeof value === 'boolean') {
        backendFilters[backendKey] = value;
      }
    });

    dispatch(fetchCandidates({ skip, limit: pageSize, org_id: orgId, filters: backendFilters }));
  }, [dispatch, orgId, currentPage, pageSize, filters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    if (funnelStatuses && funnelStatuses.length > 0) {
      setSelectedStatuses(funnelStatuses.map(status => status.id.toString()));
    }
  }, [funnelStatuses]);

  useEffect(() => {
    const openCandidateId = searchParams.get('openCandidate');
    if (openCandidateId && candidates.length > 0) {
      const candidateToOpen = candidates.find(c => c.id === parseInt(openCandidateId));
      if (candidateToOpen) {
        setSelectedCandidateId(candidateToOpen.id);
        dispatch(fetchCandidateById({ id: candidateToOpen.id, organisationId: orgId }));
        setViewModalVisible(true);
        setSearchParams({});
      }
    }
  }, [searchParams, candidates, setSearchParams, dispatch, orgId]);

  const selectedCandidate = selectedCandidateId ? currentCandidate : null;

  const getStatusKeyFromName = (statusName) => {
    if (statusName.includes('Новый')) return 'new';
    if (statusName.includes('Перезвонить')) return 'call_back';
    if (statusName.includes('Собеседование')) return 'interview';
    if (statusName.includes('Сбор рекомендаций')) return 'references';
    if (statusName.includes('Предложение о работе')) return 'offer';
    if (statusName.includes('Оформление документов')) return 'documentation';
    if (statusName.includes('Трудоустроен') || statusName.includes('Завершить')) return 'hired';
    if (statusName.includes('Отклонён') || statusName.includes('Отказ')) return 'failed';
    return null;
  };

  const isCompletionStatus = (statusName) => {
    const statusKey = getStatusKeyFromName(statusName);
    return statusKey === 'hired' || statusKey === 'failed';
  };

  const handleUpdateInfo = async (id, status) => {
    const skip = (currentPage - 1) * pageSize;
    const backendFilters = {};
    Object.keys(filters).forEach(key => {
      const value = filters[key];
      if (value === null || value === undefined || value === '') return;
      if (Array.isArray(value)) {
        const filteredArray = value.filter(v => v !== null && v !== undefined && v !== '');
        if (filteredArray.length > 0) backendFilters[key] = filteredArray.join(',');
      } else if (typeof value === 'string' || typeof value === 'number') {
        backendFilters[key] = value;
      }
    });
    dispatch(fetchCandidates({ skip, limit: pageSize, org_id: orgId, filters: backendFilters }));
  }

  const handleCreateSuccess = () => {
    const skip = (currentPage - 1) * pageSize;
    const backendFilters = {};
    Object.keys(filters).forEach(key => {
      const value = filters[key];
      if (value === null || value === undefined || value === '') return;
      if (Array.isArray(value)) {
        const filteredArray = value.filter(v => v !== null && v !== undefined && v !== '');
        if (filteredArray.length > 0) backendFilters[key] = filteredArray.join(',');
      } else if (typeof value === 'string' || typeof value === 'number') {
        backendFilters[key] = value;
      }
    });
    dispatch(fetchCandidates({ skip, limit: pageSize, org_id: orgId, filters: backendFilters }));
  };

  const handleUpdate = async (formData) => {
    try {
      await dispatch(updateCandidate({ id: selectedCandidate.id, organisationId: orgId, candidate: formData })).unwrap();
      setIsEditModalOpen(false);
      setSelectedCandidateId(null);
    } catch (error) {
      message.error('Ошибка при обновлении кандидата');
    }
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteCandidate(id)).unwrap();
    } catch (error) {
      message.error('Ошибка при удалении кандидата');
    }
  };

  const handleEdit = (candidate) => {
    setSelectedCandidateId(candidate.id);
    dispatch(fetchCandidateById({ id: candidate.id, organisationId: orgId }));
    setIsEditModalOpen(true);
  };

  const handleView = (candidate) => {
    setSelectedCandidateId(candidate.id);
    dispatch(fetchCandidateById({ id: candidate.id, organisationId: orgId }));
    setViewModalVisible(true);
  };

  const handleCreateFromParser = async (candidateData) => {
    try {
      await dispatch(createCandidate({ orgId: orgId, candidate: candidateData })).unwrap();
    } catch (error) {
      message.error('Ошибка при создании кандидата из парсера');
    }
  }

  const getFiles = (candidate) => {
    return candidate?.files || [];
  };

  const handleStatusChange = async (item, newStatus, newIndex, completionData = null) => {
    let candidateId, vacancyId, originalStatus;

    if (item.candidate && item.vacancyInfo) {
      candidateId = item.candidate.id;
      vacancyId = item.vacancyInfo.vacancy_id || item.vacancyInfo.id;
      originalStatus = item.candidate.status;
    } else {
      candidateId = item.id;
      vacancyId = item.vacancy_id;
      originalStatus = item.status;
    }

    if (completionData) {
      try {
        dispatch(updateCandidateStatusOptimistic({ vacancyId: vacancyId, candidateId: candidateId, statusId: parseInt(newStatus) }));

        await dispatch(updateVacancyCandidateStatus({
          vacancyId: vacancyId,
          candidateId: candidateId,
          statusId: parseInt(newStatus),
          completionData: completionData
        })).unwrap();


        if (selectedCandidateId === candidateId) {
          await dispatch(fetchCandidateById({ id: candidateId, organisationId: orgId })).unwrap();
        }

        const skip = (currentPage - 1) * pageSize;
        const backendFilters = {};
        Object.keys(filters).forEach(key => {
          const value = filters[key];
          if (value === null || value === undefined || value === '') return;
          if (Array.isArray(value)) {
            const filteredArray = value.filter(v => v !== null && v !== undefined && v !== '');
            if (filteredArray.length > 0) backendFilters[key] = filteredArray.join(',');
          } else if (typeof value === 'string' || typeof value === 'number') {
            backendFilters[key] = value;
          }
        });
        await dispatch(fetchCandidates({ skip, limit: pageSize, org_id: orgId, filters: backendFilters })).unwrap();

      } catch (error) {
        console.error('Error updating candidate status:', error);
        dispatch(revertCandidateStatus({ vacancyId: vacancyId, candidateId: candidateId, originalStatusId: originalStatus }));
      }
      return;
    }

    const newStatusName = funnelStatuses?.find(s => s.id === parseInt(newStatus))?.name;

    if (newStatusName && isCompletionStatus(newStatusName)) {
      setPendingStatusChange({
        candidateId,
        vacancyId,
        originalStatus,
        newStatus: parseInt(newStatus),
        newStatusName
      });
      setCompletionModalVisible(true);
      return;
    }

    try {
      dispatch(updateCandidateStatusOptimistic({ vacancyId: vacancyId, candidateId: candidateId, statusId: parseInt(newStatus) }));

      await dispatch(updateVacancyCandidateStatus({
        vacancyId: vacancyId,
        candidateId: candidateId,
        statusId: parseInt(newStatus)
      })).unwrap();

      if (selectedCandidateId === candidateId) {
        await dispatch(fetchCandidateById({ id: candidateId, organisationId: orgId })).unwrap();
      }

      // Перезагружаем список кандидатов после успешного обновления статуса
      const skip = (currentPage - 1) * pageSize;
      const backendFilters = {};
      Object.keys(filters).forEach(key => {
        const value = filters[key];
        if (value === null || value === undefined || value === '') return;
        if (Array.isArray(value)) {
          const filteredArray = value.filter(v => v !== null && v !== undefined && v !== '');
          if (filteredArray.length > 0) backendFilters[key] = filteredArray.join(',');
        } else if (typeof value === 'string' || typeof value === 'number') {
          backendFilters[key] = value;
        }
      });
      await dispatch(fetchCandidates({ skip, limit: pageSize, org_id: orgId, filters: backendFilters })).unwrap();

    } catch (error) {
      console.error('Error updating candidate status:', error);
      dispatch(revertCandidateStatus({ vacancyId: vacancyId, candidateId: candidateId, originalStatusId: originalStatus }));
    }
  };

  const handleCommentSubmit = async (id, comment) => {
    try {
      await dispatch(updateCandidate({ id, organisationId: orgId, candidate: { comment } })).unwrap();
      message.success('Комментарий добавлен');
    } catch (error) {
      message.error('Ошибка при добавлении комментария');
    }
  };

  const handleCompletionConfirm = async (completionData) => {
    if (!pendingStatusChange) return;

    const { candidateId, vacancyId, originalStatus, newStatus } = pendingStatusChange;

    try {
      dispatch(updateCandidateStatusOptimistic({
        vacancyId: vacancyId,
        candidateId: candidateId,
        statusId: newStatus
      }));

      await dispatch(updateVacancyCandidateStatus({
        vacancyId: vacancyId,
        candidateId: candidateId,
        statusId: newStatus,
        completionData: completionData
      })).unwrap();

      if (selectedCandidateId === candidateId) {
        await dispatch(fetchCandidateById({ id: candidateId, organisationId: orgId })).unwrap();
      }

      // Перезагружаем список кандидатов
      const skip = (currentPage - 1) * pageSize;
      const backendFilters = {};
      Object.keys(filters).forEach(key => {
        const value = filters[key];
        if (value === null || value === undefined || value === '') return;
        if (Array.isArray(value)) {
          const filteredArray = value.filter(v => v !== null && v !== undefined && v !== '');
          if (filteredArray.length > 0) backendFilters[key] = filteredArray.join(',');
        } else if (typeof value === 'string' || typeof value === 'number') {
          backendFilters[key] = value;
        }
      });
      await dispatch(fetchCandidates({ skip, limit: pageSize, org_id: orgId, filters: backendFilters })).unwrap();

      setCompletionModalVisible(false);
      setPendingStatusChange(null);

    } catch (error) {
      console.error('Error updating candidate status:', error);
      dispatch(revertCandidateStatus({
        vacancyId: vacancyId,
        candidateId: candidateId,
        originalStatusId: originalStatus
      }));
    }
  };

  const handleCompletionCancel = () => {
    setCompletionModalVisible(false);
    setPendingStatusChange(null);
  };

  const loading = candidatesLoading || vacanciesLoading || usersLoading;

  return (
    <div className={styles.container} style={{ paddingBottom: '0px' }}>
      <div className={styles.controls}>
        <h1 style={{ margin: '0px', marginRight: 'auto', paddingRight: '20px' }}>Кандидаты</h1>
        <Space wrap>
          {viewMode === 'list' ? (
            <>
              <Popover
                open={columnsDropdownOpen}
                onOpenChange={setColumnsDropdownOpen}
                content={
                  <div style={{ maxHeight: '70vh', overflowY: 'auto', minWidth: '200px' }}>
                    {allColumnKeys.map(key => (
                      <div key={key} style={{ padding: '4px 0' }}>
                        <Checkbox
                          checked={selectedColumns.includes(key)}
                          onChange={(e) => {
                            setSelectedColumns(prev => {
                              return prev.includes(key)
                                ? prev.filter(col => col !== key)
                                : [...prev, key];
                            });
                          }}
                        >
                          {columnLabels[key]}
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                }
                trigger="click"
                placement="bottomLeft"
              >
                <Tooltip title="Настройка отображения колонок в таблице">
                  <Button icon={<SettingOutlined />}>
                    Настройка колонок
                  </Button>
                </Tooltip>
              </Popover>
            </>
          ) : (
            <Popover
              open={statusesDropdownOpen}
              onOpenChange={setStatusesDropdownOpen}
              content={
                <div style={{ maxHeight: '70vh', overflowY: 'auto', minWidth: '200px' }}>
                  {funnelStatuses && funnelStatuses.filter(status => status.id !== 11).map(status => (
                    <div key={status.id} style={{ padding: '4px 0' }}>
                      <Checkbox
                        checked={selectedStatuses.includes(status.id.toString())}
                        onChange={(e) => {
                          setSelectedStatuses(prev => {
                            return prev.includes(status.id.toString())
                              ? prev.filter(s => s !== status.id.toString())
                              : [...prev, status.id.toString()];
                          });
                        }}
                      >
                        {status.name}
                      </Checkbox>
                    </div>
                  ))}
                </div>
              }
              trigger="click"
              placement="bottomLeft"
            >
              <Tooltip title="Настройка отображения канбана">
                <Button icon={<SettingOutlined />}>
                  Настройка канбана
                </Button>
              </Tooltip>
            </Popover>
          )}

          <Radio.Group
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            buttonStyle="solid"
          >
            <Tooltip title="Просмотр кандидатов в виде таблицы">
              <Radio.Button value="list">Список</Radio.Button>
            </Tooltip>
            <Tooltip title="Просмотр кандидатов в виде канбан-доски">
              <Radio.Button value="kanban">Канбан</Radio.Button>
            </Tooltip>
          </Radio.Group>
          <Dropdown
            overlay={
              <Menu
                onClick={({ key }) => {
                  if (isLeader) {
                    message.warning('Нет прав для создания кандидатов');
                    return;
                  }
                  switch (key) {
                    case 'new':
                      setSelectedCandidateId(null);
                      setFormModalVisible(true);
                      break;
                    case 'from_parser':
                      setParserModalVisible(true);
                      break;
                    default:
                      break;
                  }
                }}
              >
                <Menu.Item key="new" disabled={isLeader}>Новый</Menu.Item>
                <Menu.Item key="from_parser" disabled={isLeader}>Из парсера</Menu.Item>
              </Menu>
            }
          >
            <Tooltip title={isLeader ? 'Нет прав для создания кандидатов' : 'Добавить кандидата'}>
              <Button type="primary" disabled={isLeader}>
                Добавить кандидата <DownOutlined />
              </Button>
            </Tooltip>
          </Dropdown>
        </Space>
      </div>

      {loading ? (
        <div>Загрузка...</div>
      ) : viewMode === 'list' ? (
        <CandidateList
          candidates={candidates}
          vacancies={vacancies}
          loading={loading}
          onUpdate={handleEdit}
          onDelete={handleDelete}
          onView={handleView}
          userRole={user?.role}
          visibleColumns={selectedColumns}
          getFiles={getFiles}
          loadVacancyData={true}
          candidateVacancyData={null}
          onStatusChange={handleStatusChange}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={(page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          }}
          filters={filters}
          onFiltersChange={setFilters}
        />
      ) : (
        <CandidateKanban
          candidates={candidates}
          vacancies={vacancies}
          loading={loading}
          onUpdate={handleEdit}
          onDelete={handleDelete}
          onView={handleView}
          onStatusChange={handleStatusChange}
          userRole={user?.role}
          organisationId={orgId}
          selectedStatuses={selectedStatuses.filter(status => status !== '11')}
        />
      )}

      <EditModal
        visible={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          setSelectedCandidateId(null);
        }}
        updateInfo={handleUpdateInfo}
        onSubmit={handleUpdate}
        vacancies={vacancies}
        candidate={selectedCandidate}
      />

      <CreateModal
        isOpen={formModalVisible}
        onCancel={() => setFormModalVisible(false)}
        updateInfo={handleUpdateInfo}
        vacancies={vacancies}
        initialValues={{
          organisation_id: orgId
        }}
        onSuccess={handleCreateSuccess}
      />

      <CandidateViewModal
        visible={viewModalVisible}
        onClose={() => {
          setViewModalVisible(false);
          setSelectedCandidateId(null);
        }}
        onCommentSubmit={handleCommentSubmit}
        candidate={selectedCandidate}
        vacancies={vacancies}
        users={users}
        getFiles={getFiles}
      />

      <ParserModal
        visible={parserModalVisible}
        onCancel={() => setParserModalVisible(false)}
        vacancies={vacancies}
        onCreateCandidate={handleCreateFromParser}
      />

      <CompletionModal
        visible={completionModalVisible}
        onCancel={handleCompletionCancel}
        onConfirm={handleCompletionConfirm}
      />
      <Outlet />
    </div>
  );
};

export default CandidatesPage;